import { ActionIcon } from '@mantine/core'
import { IconInfoSmall } from '@tabler/icons-react'
import clsx from 'clsx'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CoffeeBullet } from 'src/components/CoffeeBullet/CoffeeBullet'
import { Modal } from 'src/components/Modal/Modal'
import { RoastDegree } from 'src/components/RoastDegree/RoastDegree'
import { Text } from 'src/components/Text/Text'

import styles from './CoffeeDetailsTable.module.scss'

interface CoffeeDetailsTableRowProps {
  metaKey:
    | 'taste'
    // 👆 taste is overridden with a pipe for {adjective} | {taste} format
    | 'strength'
    | 'acidity'
    | 'variety'
    | 'origin'
    | 'organic'
    | 'roast_degree'
    | 'intensity'
    | 'style'
    | 'processing'
    | 'species'
    // this 👇 is a synthetic key
    | 'composed_variety'
    | string
  value: string | number | boolean | Array<{ title: string }> | { title: string }
  withoutModals?: boolean
}

export const CoffeeDetailsTableRow = ({ metaKey, value, withoutModals }: CoffeeDetailsTableRowProps) => {
  const { t, i18n } = useTranslation()
  const [activeModal, setActiveModal] = useState<string | null>(null)

  if (metaKey === 'organic') {
    if (!value) {
      return null
    }

    return (
      <div className={styles.rowFlex}>
        <div className={clsx(styles.header, styles.bioHeader)}>
          <img
            className={styles.bioLogo}
            src="https://cdn.coffeecircle.com/5d3eb31e-0f09-407d-a383-e4ce8254844c/organiclogo.svg"
            alt="Bio-Siegel der Europäischen Union"
            width="400"
            height="268"
            loading="lazy"
          />
        </div>

        <div className={styles.data}>{t('coffee.organic')}</div>
      </div>
    )
  }

  // roast degree
  if (typeof value === 'number' && metaKey === 'roast_degree') {
    if (!value || value < 1 || value > 9) {
      return null
    }

    return (
      <div className={styles.rowFlex}>
        <div className={styles.header}>{t('coffee.roastDegree')}</div>
        <div className={styles.data}>
          <RoastDegree value={value} withModal />
        </div>
      </div>
    )
  }

  // starke & saure
  if (typeof value === 'number' && metaKey !== 'roast_degree') {
    const maxBullets = metaKey === 'intensity' ? 9 : 6
    return (
      <>
        <div className={styles.rowFlex}>
          <div className={styles.header}>{t(`coffee.${metaKey}`)}</div>
          <div className={clsx(styles.data, styles.dataFlex)}>
            <div className={styles.bulletsWrapper}>
              {[...Array(maxBullets).keys()].map((number, index) => (
                <CoffeeBullet full={index < value} key={number} large />
              ))}
            </div>

            {!withoutModals && (
              <ActionIcon
                variant="light"
                color="grey"
                size="sm"
                onClick={() => setActiveModal(metaKey)}
                aria-label={`${metaKey} info`}
                data-analytics-id="coffee-detail-info"
              >
                <IconInfoSmall stroke={1.6} />
              </ActionIcon>
            )}
          </div>
        </div>

        {!withoutModals && (
          <Modal title={t(`coffee.${metaKey}`)} opened={activeModal === metaKey} onClose={() => setActiveModal(null)}>
            <Text variant="p">{t(`coffee.${metaKey}ModalText`)}</Text>
          </Modal>
        )}
      </>
    )
  }

  if (metaKey === 'origin' || metaKey === 'processing') {
    if (Array.isArray(value)) {
      const metaobjectValues = value.map((metaobject) => metaobject.title).join(', ')
      const rowHasModal = !withoutModals && i18n.exists(`coffee.${metaKey}ModalText`)

      return (
        <>
          <div className={styles.rowFlex}>
            <div className={styles.header}>{t(`coffee.${metaKey}`)}</div>
            <div className={clsx(styles.data, styles.dataFlex)}>
              {metaobjectValues}

              {rowHasModal && (
                <ActionIcon
                  variant="light"
                  color="grey"
                  size="sm"
                  onClick={() => setActiveModal(metaKey)}
                  aria-label={`${metaKey} info`}
                  data-analytics-id="coffee-detail-info"
                >
                  <IconInfoSmall stroke={1.6} />
                </ActionIcon>
              )}
            </div>
          </div>

          {rowHasModal && (
            <Modal title={t(`coffee.${metaKey}`)} opened={activeModal === metaKey} onClose={() => setActiveModal(null)}>
              <Text variant="p">{t(`coffee.${metaKey}ModalText`)}</Text>
            </Modal>
          )}
        </>
      )
    }
  }

  if (metaKey === 'style') {
    return (
      <div className={styles.rowFlex}>
        <div className={styles.header}>{t(`coffee.${metaKey}`)}</div>
        <div className={styles.data}>{(value as { title: string }).title}</div>
      </div>
    )
  }

  // they are displayed as "metaKey === 'composed_variety'"
  if (metaKey === 'species' || metaKey === 'variety') {
    return null
  }

  return (
    <div className={styles.rowFlex}>
      <div className={styles.header}>{t(`coffee.${metaKey}`)}</div>
      <div className={styles.data}>{value as string}</div>
    </div>
  )
}
